import React from 'react';
import { Button } from '@/components/ui/button';
import { Pencil, Key, Trash, Users, Tag } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Organization } from '../types/organization.types';
import { formatDate } from '../utils/formatUtils';
import { Badge } from '@/components/ui/badge';
import { parseLabelValue } from '@/utils/labelUtils';

interface OrganizationsTableProps {
  organizations: Organization[];
  onEditOrganization?: (orgId: string) => void;
  onGenerateInvite?: (orgId: string) => void;
  onDeleteOrganization?: (orgId: string) => void;
}

const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
  organizations,
  onEditOrganization,
  onGenerateInvite,
  onDeleteOrganization
}) => {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Tags</TableHead>
          <TableHead>Members</TableHead>
          <TableHead>Created</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {organizations.length === 0 ? (
          <TableRow>
            <TableCell colSpan={6} className="h-24 text-center">
              No organizations found.
            </TableCell>
          </TableRow>
        ) : (
          organizations.map((org) => (
            <TableRow key={org.id}>
              <TableCell className="font-medium">{org.name}</TableCell>
              <TableCell>{org.description || 'No description'}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {org.labels && parseLabelValue(org.labels).length > 0 ? (
                    parseLabelValue(org.labels).map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-xs text-muted-foreground">No tags</span>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center">
                  <Users size={16} className="mr-2 text-muted-foreground" />
                  <span>{org.membersCount}</span>
                </div>
              </TableCell>
              <TableCell>{formatDate(org.createdAt)}</TableCell>
              <TableCell className="text-right space-x-2">
                {onEditOrganization && (
                  <Button variant="ghost" size="sm" onClick={() => onEditOrganization(org.id)}>
                    <Pencil className="h-4 w-4" />
                    <span className="sr-only">Edit</span>
                  </Button>
                )}
                {onGenerateInvite && (
                  <Button variant="ghost" size="sm" onClick={() => onGenerateInvite(org.id)}>
                    <Key className="h-4 w-4" />
                    <span className="sr-only">Invite</span>
                  </Button>
                )}
                {onDeleteOrganization && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-destructive hover:text-destructive/90" 
                    onClick={() => onDeleteOrganization(org.id)}
                  >
                    <Trash className="h-4 w-4" />
                    <span className="sr-only">Delete</span>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
};

export default OrganizationsTable;
